import { useState, type FormEvent } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api, type EventDetail } from '../lib/api';
import { DISCIPLINES, TYPES, durationChoice, hm, ymd } from '../lib/domain';
import { parseDay } from '../lib/calendar';
import { LOCATION_SUGGESTIONS, MEETING_SUGGESTIONS, suggestPlace } from '../lib/places';
import { DiscIcon, ErrorBox, Icon, Loading, PageHead } from '../ui';
import { leaveForm } from '../lib/nav';
import { DateField, TimeField } from '../fields';
import { MdEditor } from '../mdeditor';
import { useToast } from '../hooks';
import { dc } from '../lib/style';
import { useBarTitle } from '../barTitle';

const DURS: [number, string][] = [
  [-1, 'Offen'],
  [30, '30 Min.'],
  [60, '1 Std.'],
  [90, '1,5 Std.'],
  [120, '2 Std.'],
  [180, '3 Std.'],
  [0, 'Mit Ende'],
];

interface FormState {
  title: string;
  discipline: string;
  type: string;
  date: string;
  time: string;
  dur: number;
  endDate: string;
  endTime: string;
  location: string;
  meeting_point: string;
  capOn: boolean;
  capacity: number;
  repeat: number;
  description: string;
  notify: boolean;
}
function fromEvent(e: EventDetail | undefined, shiftWeek: boolean): FormState {
  const tomorrow = new Date(Date.now() + 86_400_000);
  tomorrow.setHours(18, 0, 0, 0);
  const plusWeek = (d: Date) => {
    const x = new Date(d);
    if (shiftWeek) x.setDate(x.getDate() + 7); // local calendar days: 18:00 stays 18:00 across DST
    return x;
  };
  const start = e ? plusWeek(new Date(e.starts_at)) : tomorrow;
  return {
    title: e?.title || '',
    discipline: e?.discipline || 'run',
    type: e?.type || 'training',
    date: ymd(start),
    time: hm(start),
    dur: durationChoice(e),
    endDate: e?.ends_at ? ymd(plusWeek(new Date(e.ends_at))) : ymd(new Date(start.getTime() + 2 * 86_400_000)),
    endTime: e?.ends_at ? hm(new Date(e.ends_at)) : '16:00',
    location: e?.location || '',
    meeting_point: e?.meeting_point || '',
    capOn: !!e?.capacity,
    capacity: e?.capacity || 12,
    repeat: 0,
    description: e?.description || '',
    notify: true,
  };
}

/** Text when the server skipped the club-wide notification (per-person hourly limit). */
const notifyNote = (wanted: boolean) =>
  wanted ? 'Gespeichert – Benachrichtigung übersprungen (zu viele in der letzten Stunde)' : 'Gespeichert';

/** Loads the source event (edit / template) first, then renders the form with fixed initial values. */
export default function EventForm() {
  const { id } = useParams();
  const [params] = useSearchParams();
  const editId = id ? Number(id) : null;
  useBarTitle(editId ? 'Termin bearbeiten' : 'Termin anlegen'); // also while loading
  const templateId = params.get('vorlage') ? Number(params.get('vorlage')) : null;
  const srcId = editId ?? templateId;
  const src = useQuery({
    queryKey: ['event', srcId],
    queryFn: () => api<EventDetail>(`/events/${srcId}`),
    enabled: !!srcId,
  });
  if (src.error) return <ErrorBox error={src.error} />;
  if (srcId && !src.data) return <Loading />;
  const initial = fromEvent(src.data, templateId !== null);
  // "Termin an diesem Tag" from the calendar: ?datum=yyyy-mm-dd (new events only)
  const day = params.get('datum');
  if (!srcId && parseDay(day)) {
    initial.date = day!;
    // "Mit Ende" defaults to the same day, two hours after the start (never before it)
    initial.endDate = day!;
    const [h, m] = initial.time.split(':').map(Number);
    initial.endTime = `${String(Math.min(23, h + 2)).padStart(2, '0')}:${String(h + 2 > 23 ? 59 : m).padStart(2, '0')}`;
  }
  return <EventFormBody key={srcId ?? `new-${day ?? ''}`} initial={initial} editId={editId} />;
}

function EventFormBody({ initial, editId }: { initial: FormState; editId: number | null }) {
  const [f, setF] = useState<FormState>(initial);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const nav = useNavigate();
  const qc = useQueryClient();
  const toast = useToast();
  const set = <K extends keyof FormState>(k: K, v: FormState[K]) => setF(prev => ({ ...prev, [k]: v }));
  // Typical place is pre-filled from discipline/type/day until the user types a place themselves
  const [placeTouched, setPlaceTouched] = useState(editId !== null || !!initial.location || !!initial.meeting_point);
  const setAndSuggest = <K extends 'discipline' | 'type' | 'date'>(k: K, v: FormState[K]) =>
    setF(prev => {
      const next = { ...prev, [k]: v };
      if (placeTouched || !next.date) return next; // no suggestion while the typed date is incomplete
      const p = suggestPlace(next.discipline, next.type, new Date(`${next.date}T12:00`));
      return { ...next, location: p.location, meeting_point: p.meeting_point };
    });

  async function submit(ev: FormEvent) {
    ev.preventDefault();
    setError('');
    if (!f.title.trim()) return setError('Bitte gib dem Termin einen Titel.');
    const start = new Date(`${f.date}T${f.time}`);
    if (isNaN(start.getTime())) return setError('Bitte Datum und Uhrzeit prüfen.');
    let ends_at: string | null = null;
    if (f.dur === 0) {
      const end = new Date(`${f.endDate}T${f.endTime}`); // empty while the typed value is invalid → error below
      if (isNaN(end.getTime())) return setError('Bitte das Ende prüfen.');
      if (end <= start) return setError('Das Ende liegt vor dem Beginn.');
      ends_at = end.toISOString();
    }
    const body = {
      title: f.title.trim(),
      discipline: f.discipline,
      type: f.type,
      starts_at: start.toISOString(),
      ends_at,
      duration_min: f.dur > 0 ? f.dur : null,
      location: f.location.trim(),
      meeting_point: f.meeting_point.trim(),
      capacity: f.capOn ? f.capacity : null,
      description: f.description,
      notify: f.notify,
    };
    setBusy(true);
    try {
      if (editId) {
        const r = await api<{ changes: number; notified: boolean }>(`/events/${editId}`, { method: 'PATCH', body });
        toast(
          !r.changes
            ? 'Keine Änderungen'
            : r.notified
              ? 'Gespeichert – alle sehen die Änderungen'
              : notifyNote(f.notify),
        );
        await qc.invalidateQueries({ queryKey: ['event', editId] });
        void qc.invalidateQueries({ queryKey: ['events'] });
        leaveForm(nav, `/termine/${editId}`); // edit: back to the event
      } else {
        const r = await api<{ id: number; ids: number[]; notified: boolean }>('/events', {
          method: 'POST',
          body: { ...body, repeat_weeks: f.repeat },
        });
        const what = r.ids.length > 1 ? `${r.ids.length} Termine angelegt` : 'Termin angelegt';
        toast(
          r.notified ? `${what} – alle werden benachrichtigt` : `${what}${f.notify ? ' – ' + notifyNote(true) : ''}`,
        );
        void qc.invalidateQueries({ queryKey: ['events'] });
        void nav(`/termine/${r.id}`, { replace: true }); // new: the form is replaced by the event
      }
    } catch (e) {
      setError((e as Error).message);
      setBusy(false);
    }
  }

  return (
    <>
      <PageHead title={editId ? 'Termin bearbeiten' : 'Termin anlegen'} />
      <form className="form" onSubmit={ev => void submit(ev)} noValidate style={{ maxWidth: 820 }}>
        <label className="fl">
          <span>Titel</span>
          <input
            value={f.title}
            onChange={e => set('title', e.target.value)}
            maxLength={150}
            required
            placeholder="z. B. Koppeltraining Rad + Lauf"
            autoFocus={!editId}
          />
        </label>
        <fieldset className="fl">
          <legend>Disziplin</legend>
          <div className="tiles">
            {Object.entries(DISCIPLINES).map(([k, l]) => (
              <label key={k} className="tile" style={dc(k)}>
                <input
                  type="radio"
                  name="disc"
                  checked={f.discipline === k}
                  onChange={() => setAndSuggest('discipline', k)}
                />
                <DiscIcon disc={k} size="sm" />
                {l}
              </label>
            ))}
          </div>
        </fieldset>
        <fieldset className="fl">
          <legend>Art</legend>
          <div className="pills">
            {Object.entries(TYPES).map(([k, l]) => (
              <label key={k} className="pill-opt">
                <input type="radio" name="type" checked={f.type === k} onChange={() => setAndSuggest('type', k)} />
                <span>{l}</span>
              </label>
            ))}
          </div>
        </fieldset>
        <div className="grid3">
          <DateField label="Datum" value={f.date} onChange={v => setAndSuggest('date', v)} required />
          <TimeField label="Uhrzeit" value={f.time} onChange={v => set('time', v)} required />
        </div>
        <fieldset className="fl">
          <legend>Dauer</legend>
          <div className="pills">
            {DURS.map(([v, l]) => (
              <label key={v} className="pill-opt">
                <input type="radio" name="dur" checked={f.dur === v} onChange={() => set('dur', v)} />
                <span>{l}</span>
              </label>
            ))}
          </div>
          {f.dur === 0 && (
            <div className="grid2" style={{ marginTop: 6 }}>
              <DateField label="Ende am" value={f.endDate} onChange={v => set('endDate', v)} />
              <TimeField label="Ende um" value={f.endTime} onChange={v => set('endTime', v)} />
            </div>
          )}
        </fieldset>
        <div className="grid2">
          <label className="fl">
            <span>Ort</span>
            <input
              value={f.location}
              list="places-location"
              onChange={e => {
                setPlaceTouched(true);
                set('location', e.target.value);
              }}
              maxLength={200}
              placeholder="z. B. Hallenbad Albgaubad"
            />
          </label>
          <label className="fl">
            <span>Treffpunkt</span>
            <input
              value={f.meeting_point}
              list="places-meeting"
              onChange={e => {
                setPlaceTouched(true);
                set('meeting_point', e.target.value);
              }}
              maxLength={200}
              placeholder="z. B. 10 Min. vorher am Eingang"
            />
          </label>
          <datalist id="places-location">
            {LOCATION_SUGGESTIONS.map(p => (
              <option key={p} value={p} />
            ))}
          </datalist>
          <datalist id="places-meeting">
            {MEETING_SUGGESTIONS.map(p => (
              <option key={p} value={p} />
            ))}
          </datalist>
        </div>
        <div className="grid2">
          <div className="fl">
            <span className="fl-l">Plätze</span>
            <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
              <label className="switch">
                <input
                  type="checkbox"
                  checked={f.capOn}
                  onChange={e => set('capOn', e.target.checked)}
                  aria-label="Plätze begrenzen"
                />
                <span />
              </label>
              {f.capOn && (
                <span className="stepper">
                  <button
                    type="button"
                    onClick={() => set('capacity', Math.max(1, f.capacity - 1))}
                    aria-label="Weniger"
                  >
                    −
                  </button>
                  <input
                    type="number"
                    min={1}
                    max={1000}
                    value={f.capacity}
                    onChange={e => set('capacity', Math.max(1, Number(e.target.value) || 1))}
                    aria-label="Maximale Teilnehmer"
                  />
                  <button type="button" onClick={() => set('capacity', f.capacity + 1)} aria-label="Mehr">
                    +
                  </button>
                </span>
              )}
              <span className="hint">{f.capOn ? 'Danach Warteliste' : 'unbegrenzt'}</span>
            </div>
          </div>
          {!editId && (
            <label className="fl">
              <span>Wiederholen</span>
              <select aria-label="Wiederholen" value={f.repeat} onChange={e => set('repeat', Number(e.target.value))}>
                <option value={0}>Einmalig</option>
                <option value={4}>Wöchentlich · 4 Wochen</option>
                <option value={8}>Wöchentlich · 8 Wochen</option>
                <option value={12}>Wöchentlich · 12 Wochen</option>
              </select>
            </label>
          )}
        </div>
        <MdEditor
          id="ev-desc"
          label="Beschreibung"
          value={f.description}
          onChange={v => set('description', v)}
          placeholder="Was ist geplant? Was sollen alle mitbringen?"
        />
        <div className="notify-box">
          <label style={{ display: 'flex', gap: 10, alignItems: 'center', fontWeight: 600 }}>
            <span className="switch">
              <input
                type="checkbox"
                checked={f.notify}
                onChange={e => set('notify', e.target.checked)}
                aria-label="Mitglieder benachrichtigen"
              />
              <span />
            </span>
            {editId
              ? 'Alle über die Änderungen informieren (E-Mail mit markierten Änderungen und Push)'
              : 'Alle Mitglieder sofort benachrichtigen (E-Mail und Push)'}
          </label>
        </div>
        {error && (
          <p className="err" role="alert">
            {error}
          </p>
        )}
        <div className="form-actions">
          <button type="button" className="btn btn-ghost" onClick={() => void nav(-1)}>
            Abbrechen
          </button>
          <button className="btn btn-primary" disabled={busy}>
            <Icon name="check" sw={2} />
            {editId ? 'Änderungen speichern' : 'Termin anlegen'}
          </button>
        </div>
      </form>
    </>
  );
}
